import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FaBoxOpen,
  FaTag,
  FaImage,
  FaAlignLeft,
  FaStar,
} from "react-icons/fa";
import BASE_URL from "../../BASEURL";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

export default function ProductAdd() {
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || BASE_URL;

  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);

  const [form, setForm] = useState({
    productName: "",
    categoryId: "",
    rating: "",
    description: "",
    isActive: true,
    isBestSeller: false,
    isSale: false,
    sizes: [
      {
        size: "",
        price: "",
        discountPrice: "",
      },
    ],
  });

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/category`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.success === false) {
          throw new Error(data?.message || "Failed to load categories");
        }
        setCategories(data?.data || data || []);
      } catch (e) {
        toast.error(e?.message || "Failed to load categories");
      }
    };
    loadCategories();
  }, [backendUrl]);

  // HANDLE INPUT CHANGE
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSizeChange = (index, field, value) => {
    const updatedSizes = [...form.sizes];
    updatedSizes[index][field] = value;
    setForm((prev) => ({
      ...prev,
      sizes: updatedSizes,
    }));
  };

  const addSize = () => {
    setForm((prev) => ({
      ...prev,
      sizes: [
        ...prev.sizes,
        { size: "", price: "", discountPrice: "" },
      ],
    }));
  };

  const removeSize = (index) => {
    if (form.sizes.length === 1) return;
    const updatedSizes = form.sizes.filter((_, i) => i !== index);
    setForm((prev) => ({
      ...prev,
      sizes: updatedSizes,
    }));
  };

  // SUBMIT FORM
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem("adminToken");
      const fd = new FormData();

      fd.append("productName", form.productName);
      fd.append("rating", String(Number(form.rating || 0)));
      fd.append("categoryId", form.categoryId);
      fd.append("description", form.description);
      fd.append("isActive", String(form.isActive));
      fd.append("isBestSeller", String(form.isBestSeller));
      fd.append("isSale", String(form.isSale));

      // Send sizes array
      fd.append(
        "sizes",
        JSON.stringify(
          form.sizes.map((item) => ({
            size: item.size,
            price: Number(item.price),
            discountPrice: Number(item.discountPrice),
          }))
        )
      );

      // Images
      imageFiles.forEach((file) => {
        fd.append("images", file);
      });

      const res = await fetch(`${backendUrl}/api/product/create`, {
        method: "POST",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: fd,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || "Failed to add product");
      }

      toast.success("Product added successfully");
      navigate("/admin/products");
    } catch (err) {
      toast.error(err?.message || "Failed to add product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-5">
      <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden">
        {/* HEADER */}
        <div className="bg-black px-8 py-6">
          <h2 className="text-3xl font-bold text-white">Add Product</h2>
          <p className="text-gray-300 mt-1 text-sm">
            Add a new product to your catalog
          </p>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit} className="p-8 space-y-7">
          {/* PRODUCT NAME */}
          <div>
            <label className="font-semibold mb-2 flex items-center gap-2">
              <FaBoxOpen /> Product Name
            </label>
            <input
              type="text"
              name="productName"
              value={form.productName}
              onChange={handleChange}
              placeholder="Enter product name"
              className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* CATEGORY */}
            <div>
              <label className="font-semibold mb-2 block">Category</label>
              <select
                name="categoryId"
                value={form.categoryId}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-xl px-4 py-3"
                required
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* RATING */}
            <div>
              <label className="font-semibold mb-2 flex items-center gap-2 block">
                <FaStar /> Rating
              </label>
              <input
                type="number"
                step="0.1"
                name="rating"
                value={form.rating}
                onChange={handleChange}
                placeholder="0.0"
                className="w-full border border-gray-300 rounded-xl px-4 py-3"
              />
            </div>
          </div>

          {/* SIZES */}
          <div className="border border-gray-200 p-5 rounded-2xl bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <label className="font-semibold flex items-center gap-2 m-0">
                <FaTag /> Sizes & Prices
              </label>
              <button
                type="button"
                onClick={addSize}
                className="bg-black text-white px-4 py-2 rounded-xl text-sm hover:opacity-90 transition"
              >
                + Add Size
              </button>
            </div>

            {form.sizes.map((item, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                <input
                  type="text"
                  placeholder="Size (L, XL, 500g)"
                  value={item.size}
                  onChange={(e) => handleSizeChange(index, "size", e.target.value)}
                  className="border border-gray-300 px-4 py-3 rounded-xl w-full"
                  required
                />
                <input
                  type="number"
                  placeholder="Price"
                  value={item.price}
                  onChange={(e) => handleSizeChange(index, "price", e.target.value)}
                  className="border border-gray-300 px-4 py-3 rounded-xl w-full"
                  required
                />
                <input
                  type="number"
                  placeholder="Discount Price"
                  value={item.discountPrice}
                  onChange={(e) => handleSizeChange(index, "discountPrice", e.target.value)}
                  className="border border-gray-300 px-4 py-3 rounded-xl w-full"
                  required
                />
                <button
                  type="button"
                  onClick={() => removeSize(index)}
                  className="bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 rounded-xl px-4 py-3 w-full transition"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          {/* IMAGES */}
          <div>
            <label className="font-semibold mb-2 flex items-center gap-2">
              <FaImage /> Product Images
            </label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setImageFiles(Array.from(e.target.files || []))}
              className="mt-2 block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-black file:text-white
                hover:file:bg-gray-800"
            />
            {imageFiles.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {imageFiles.map((file, index) => (
                  <div key={index} className="border rounded-2xl overflow-hidden h-32 bg-gray-100">
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="font-semibold mb-2 flex items-center gap-2">
              <FaAlignLeft /> Description
            </label>
            <ReactQuill
              theme="snow"
              value={form.description}
              onChange={(value) => setForm((prev) => ({ ...prev, description: value }))}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ color: [] }, { background: [] }],
                  [{ align: [] }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link"],
                  ["clean"],
                ],
              }}
              className="bg-white rounded-xl"
            />
          </div>

          {/* CHECKBOXES */}
          <div className="flex flex-wrap gap-6 pt-2">
            <label className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl cursor-pointer hover:bg-gray-100 transition">
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span className="font-medium">Active</span>
            </label>
            <label className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl cursor-pointer hover:bg-gray-100 transition">
              <input
                type="checkbox"
                name="isBestSeller"
                checked={form.isBestSeller}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span className="font-medium">Best Seller</span>
            </label>
            <label className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl cursor-pointer hover:bg-gray-100 transition">
              <input
                type="checkbox"
                name="isSale"
                checked={form.isSale}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span className="font-medium">On Sale</span>
            </label>
          </div>

          {/* BUTTONS */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate("/admin/products")}
              className="px-6 py-3 rounded-xl border border-gray-300 hover:bg-gray-100 transition font-medium"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 rounded-xl bg-black text-white hover:bg-gray-800 disabled:opacity-50 transition font-medium"
            >
              {saving ? "Saving..." : "Save Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
